import React, {
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import clsx from 'clsx';
import keyBy from 'lodash/keyBy';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from 'pages/Dashboard/components/Button';

type ComponentParts = 'root' | 'button';

type Props<T> = {
  onChange: (value: T[]) => void;
  value?: T[];
  options?: T[];
  className?: string;
  getLabel: (option: T) => ReactNode | string;
  getId: (option: T) => string | number;
  classes?: Partial<Record<ComponentParts, string>>;
  readonly?: boolean;
};
export default function MultiChoiceGroup<T>({
  onChange,
  value = [],
  options = [],
  classes,
  getLabel,
  getId,
  readonly = false,
}: Props<T>) {
  const selectedValueById = useMemo(() => keyBy(value, getId), [value, getId]);
  const handleToggle = useCallback((option: T) => {
    if (!readonly) {
      const itemId = getId(option);
      if (itemId in selectedValueById) {
        const index = value.findIndex((option) => getId(option) === itemId);
        onChange([
          ...value.slice(0, index),
          ...value.slice(index + 1),
        ]);
      } else {
        onChange([
          ...value,
          option,
        ]);
      }
    }
  }, [value, readonly]);

  return (
    <ButtonGroup
      className={clsx(classes?.root, {
        '!cursor-default': readonly,
      })}
      variant="contained"
      disableElevation
      disabled={readonly}
    >
      {options.map((option) => {
        const id = getId(option);
        const selected = id in selectedValueById;
        return (
          <Button
            key={id}
            variant={selected ? 'contained' : 'outlined'}
            color={selected ? 'primary' : undefined}
            onClick={() => { handleToggle(option); }}
            className={clsx(classes?.button, {
              '!bg-blue-500 !text-white': selected,
              '!cursor-default': readonly,
            })}
          >
            {getLabel(option)}
          </Button>
        );
      })}
    </ButtonGroup>
  );
}
